import { useState, useEffect } from 'react'
import { Bookmark, Trash2, Check, Loader2, Pencil, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useGetSavedFiltersQuery,
  useCreateSavedFilterMutation,
  useDeleteSavedFilterMutation,
  useUpdateSavedFilterMutation,
  type SavedFilter,
} from '@/redux/service/savedFilters'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface SavedFiltersPanelProps {
  open: boolean
  onClose: () => void
  page: string
  currentFilters: Record<string, unknown>
  onApply: (filters: Record<string, unknown>) => void
  startInSaveMode?: boolean
}

export default function SavedFiltersPanel({
  open,
  onClose,
  page,
  currentFilters,
  onApply,
  startInSaveMode = false,
}: SavedFiltersPanelProps) {
  const [saveMode, setSaveMode] = useState(startInSaveMode)
  const [filterName, setFilterName] = useState('')
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  useEffect(() => {
    if (open) {
      setSaveMode(startInSaveMode)
      setFilterName('')
      setRenamingId(null)
      setRenameValue('')
      setConfirmDeleteId(null)
    }
  }, [open, startInSaveMode])

  const { data, isLoading } = useGetSavedFiltersQuery(page, { skip: !open })
  const [createSavedFilter, { isLoading: isCreating }] =
    useCreateSavedFilterMutation()
  const [deleteSavedFilter, { isLoading: isDeleting }] =
    useDeleteSavedFilterMutation()
  const [updateSavedFilter, { isLoading: isUpdating }] =
    useUpdateSavedFilterMutation()

  const filters = data?.data ?? []
  const filterToDelete =
    filters.find(filter => filter.id === confirmDeleteId) ?? null

  const handleSave = async () => {
    const name = filterName.trim()
    if (!name) return
    try {
      await createSavedFilter({
        name,
        page,
        filters: currentFilters,
      }).unwrap()
      toast.success('Filter preset saved')
      setFilterName('')
      setSaveMode(false)
    } catch {
      toast.error('Failed to save filter preset')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteSavedFilter(id).unwrap()
      toast.success('Filter preset deleted')
      setConfirmDeleteId(null)
    } catch {
      toast.error('Failed to delete filter preset')
    }
  }

  const handleStartRename = (filter: SavedFilter) => {
    setRenamingId(filter.id)
    setRenameValue(filter.name)
  }

  const handleRename = async (id: number) => {
    const name = renameValue.trim()
    if (!name) return
    try {
      await updateSavedFilter({ id, name }).unwrap()
      toast.success('Filter preset renamed')
      setRenamingId(null)
      setRenameValue('')
    } catch {
      toast.error('Failed to rename filter preset')
    }
  }

  const handleApply = (filter: SavedFilter) => {
    onApply(filter.filters)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Bookmark className='h-4 w-4' />
            Saved Filters
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-3'>
          {saveMode ? (
            <div className='flex gap-2'>
              <Input
                placeholder='Filter preset name...'
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                className='h-8 text-xs'
                autoFocus
              />
              <Button
                size='sm'
                onClick={handleSave}
                disabled={!filterName.trim() || isCreating}
                className='h-8 px-3 text-xs shrink-0'
              >
                {isCreating ? (
                  <Loader2 className='h-3 w-3 animate-spin' />
                ) : (
                  'Save'
                )}
              </Button>
              <Button
                size='sm'
                variant='ghost'
                onClick={() => {
                  setSaveMode(false)
                  setFilterName('')
                }}
                className='h-8 px-3 text-xs shrink-0'
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              size='sm'
              variant='outline'
              onClick={() => setSaveMode(true)}
              className='w-full h-8 text-xs gap-2'
            >
              <Bookmark className='h-3 w-3' />
              Save current filters as preset
            </Button>
          )}

          <div className='border-t border-border pt-3'>
            {isLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
              </div>
            ) : filters.length === 0 ? (
              <div className='text-center py-8 text-xs text-muted-foreground'>
                No saved filter presets yet.
              </div>
            ) : (
              <div className='space-y-1.5 max-h-[320px] overflow-y-auto'>
                {filters.map((filter: SavedFilter) => (
                  <div
                    key={filter.id}
                    className='flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors group'
                  >
                    {renamingId === filter.id ? (
                      <div className='flex flex-1 items-center gap-2 min-w-0'>
                        <Input
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRename(filter.id)
                            if (e.key === 'Escape') setRenamingId(null)
                          }}
                          className='h-7 text-xs flex-1'
                          autoFocus
                        />
                        <Button
                          size='sm'
                          onClick={() => handleRename(filter.id)}
                          disabled={!renameValue.trim() || isUpdating}
                          className='h-7 px-2 text-xs shrink-0'
                        >
                          {isUpdating ? (
                            <Loader2 className='h-3 w-3 animate-spin' />
                          ) : (
                            <Check className='h-3 w-3' />
                          )}
                        </Button>
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => setRenamingId(null)}
                          className='h-7 w-7 p-0 shrink-0'
                        >
                          <X className='h-3 w-3' />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className='flex-1 min-w-0'>
                          <p className='text-xs font-medium truncate'>
                            {filter.name}
                          </p>
                          <p className='text-[10px] text-muted-foreground'>
                            {format(new Date(filter.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => handleApply(filter)}
                          className='h-7 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity'
                        >
                          <Check className='h-3 w-3 mr-1' />
                          Apply
                        </Button>
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => handleStartRename(filter)}
                          className='h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity'
                        >
                          <Pencil className='h-3 w-3' />
                        </Button>
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => setConfirmDeleteId(filter.id)}
                          disabled={isDeleting}
                          className='h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity'
                        >
                          <Trash2 className='h-3 w-3' />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <Dialog
        open={!!filterToDelete}
        onOpenChange={nextOpen => !nextOpen && setConfirmDeleteId(null)}
      >
        <DialogContent className='max-w-[360px] rounded-xl border-border bg-background p-5 shadow-xl'>
          <DialogHeader className='space-y-3 text-left'>
            <div className='h-10 w-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center'>
              <Trash2 className='h-4.5 w-4.5' />
            </div>
            <div className='space-y-1'>
              <DialogTitle className='text-base font-semibold text-foreground'>
                Delete Saved Filter
              </DialogTitle>
              <DialogDescription className='text-xs leading-5 text-muted-foreground'>
                Are you sure you want to delete{' '}
                <span className='font-semibold text-foreground'>
                  {filterToDelete?.name}
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </div>
          </DialogHeader>
          <div className='mt-5 flex justify-end gap-2'>
            <Button
              variant='ghost'
              className='h-9 px-4 text-xs font-medium'
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              disabled={!filterToDelete || isDeleting}
              onClick={() => filterToDelete && handleDelete(filterToDelete.id)}
              className='h-9 px-4 text-xs font-medium'
            >
              {isDeleting ? (
                <div className='flex items-center gap-2'>
                  <Loader2 className='w-4 h-4 animate-spin' />
                  <span>Deleting...</span>
                </div>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
